/* eslint-disable @typescript-eslint/no-explicit-any */
import { z } from 'zod';
import { AxiosResponse } from "axios";
import api from '../axiosInstance'

interface APIRequestParsedResult<T> {
    response: AxiosResponse<any, any>;
    data: z.infer<T>;
}

interface APIRequestGetOptions<T extends z.ZodTypeAny | undefined> {
    schema?: T;
    logSuccess?: boolean;
    logEmptyWarning?: boolean;
    logErrors?: boolean;
}

interface APIRequestPostOptions<U, T extends z.ZodTypeAny | undefined> {
    data?: U;
    schema?: T;
    logSuccess?: boolean;
    logEmptyWarning?: boolean;
    logErrors?: boolean;
}

export function APIRequestGet<T extends z.ZodTypeAny>(url: string, options?: APIRequestGetOptions<T>):
    Promise<APIRequestParsedResult<T>>;

export function APIRequestGet(url: string, options?: APIRequestGetOptions<undefined>):
    Promise<AxiosResponse<any, any>>;

export function APIRequestGet<T extends z.ZodTypeAny>(url: string, options?: APIRequestGetOptions<T | undefined>):
    Promise<APIRequestParsedResult<T>> | Promise<AxiosResponse<any, any>> {

    type APIRequestResolveValue = {
        response: AxiosResponse<any, any>;
        data: z.infer<T>;
    }

    const resultingOptions: APIRequestGetOptions<T | undefined> = {
        logSuccess: false,
        logEmptyWarning: true,
        logErrors: true,
        ...options
    };

    if (resultingOptions.schema) {
        return new Promise((resolve: (value: APIRequestResolveValue) => void, reject) => {
            api.get(url)
                .then((response) => {
                    try {
                        if (!response.data) {
                            if (resultingOptions.logEmptyWarning) {
                                console.warn(`${url} get returned empty response`, response);
                            }
                            reject(response);
                            return;
                        }
                        if (!resultingOptions.schema) {
                            if (resultingOptions.logErrors) {
                                console.error("APIRequestGet: type error");
                                reject(response);
                                return;
                            }
                            resolve({ response: response, data: response.data });
                            return;
                        }
                        const result = resultingOptions.schema.safeParse(response.data);
                        if (result.success) {
                            if (resultingOptions.logSuccess) {
                                console.log(`${url} get request finished successfully`, { response: response, data: result.data });
                            }
                            resolve({ response: response, data: result.data });
                        } else {
                            if (resultingOptions.logErrors) {
                                console.error(`${url} get response parsing failed`, response.data, result.error);
                                reject(result.error);
                                return;
                            }
                            resolve({ response: response, data: response.data });
                            return;
                        }
                    } catch (error) {
                        if (resultingOptions.logErrors) {
                            console.error("APIRequestGet: Unexpected error", error);
                            reject(error);
                            return;
                        }
                        resolve({ response: response, data: response.data });
                        return;
                    }
                })
                .catch((error) => {
                    if (resultingOptions.logErrors) {
                        console.error(`${url} get request failed`, error);
                    }
                    reject(error);
                });
        });
    } else {
        return new Promise((resolve: (value: AxiosResponse<any, any>) => void, reject) => {
            api.get(url)
                .then((response) => {
                    if (resultingOptions.logSuccess) {
                        console.log(`${url} get request finished successfully`, response);
                    }
                    resolve(response);
                })
                .catch((error) => {
                    if (resultingOptions.logErrors) {
                        console.error(`${url} get request failed`, error);
                    }
                    reject(error);
                });
        });
    }
}

export function APIRequestPost<U, T extends z.ZodTypeAny>(url: string, options?: APIRequestPostOptions<U, T>):
    Promise<APIRequestParsedResult<T>>;

export function APIRequestPost<U>(url: string, options?: APIRequestPostOptions<U, undefined>):
    Promise<AxiosResponse<any, any>>;

export function APIRequestPost<U, T extends z.ZodTypeAny>(url: string, options?: APIRequestPostOptions<U, T | undefined>):
    Promise<APIRequestParsedResult<T>> | Promise<AxiosResponse<any, any>> {

    type APIRequestResolveValue = {
        response: AxiosResponse<any, any>;
        data: z.infer<T>;
    }

    const resultingOptions: APIRequestPostOptions<U, T | undefined> = {
        logSuccess: false,
        logEmptyWarning: true,
        logErrors: true,
        ...options
    };

    if (resultingOptions.schema) {
        return new Promise((resolve: (value: APIRequestResolveValue) => void, reject) => {
            api.post(url, resultingOptions.data)
                .then((response) => {
                    try {
                        if (!response.data) {
                            if (resultingOptions.logEmptyWarning) {
                                console.warn(`${url} post returned empty response`, response);
                            }
                            reject(response);
                            return;
                        }
                        if (!resultingOptions.schema) {
                            if (resultingOptions.logErrors) {
                                console.error("APIRequestPost: type error");
                            }
                            reject(response);
                            return;
                        }
                        const result = resultingOptions.schema.safeParse(response.data);
                        if (result.success) {
                            if (resultingOptions.logSuccess) {
                                console.log(`${url} post request finished successfully`, { response: response, data: result.data });
                            }
                            resolve({ response: response, data: result.data });
                        } else {
                            if (resultingOptions.logErrors) {
                                console.error(`${url} post response parsing failed`, response.data, result.error);
                            }
                            reject(result.error);
                        }

                    } catch (error) {
                        if (resultingOptions.logErrors) {
                            console.error("APIRequestPost: Unexpected error", error);
                        }
                        reject(error);
                    }
                })
                .catch((error) => {
                    if (resultingOptions.logErrors) {
                        console.error(`${url} post request failed`, error);
                    }
                    reject(error);
                });
        });
    } else {
        return new Promise((resolve: (value: AxiosResponse<any, any>) => void, reject) => {
            api.post(url, resultingOptions.data)
                .then((response) => {
                    if (resultingOptions.logSuccess) {
                        console.log(`${url} post request finished successfully`, response);
                    }
                    resolve(response);
                })
                .catch((error) => {
                    if (resultingOptions.logErrors) {
                        console.error(`${url} post request failed`, error);
                    }
                    reject(error);
                });
        });
    }
}
